import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, Inbox, FileText, MessageSquareQuote, ShieldCheck, Check, Plus, Trash2, Edit, X, TrendingUp, TrendingDown, Rocket, ScrollText } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';

const RELIABILITY_KEYS = ['verified_fact', 'testimonial', 'opinion', 'hypothesis', 'interpretation', 'unverified_data'];
const CATEGORY_KEYS = ['diritti', 'salute', 'consenso_informato', 'giustizia', 'lavoro', 'famiglia', 'tecnologia', 'privacy', 'educazione', 'psicologia', 'filosofia', 'etica'];
const PROJECT_STATUS = ['idea', 'planning', 'active', 'completed', 'paused'];

const emptyArticle = { title_it: '', title_en: '', excerpt_it: '', excerpt_en: '', body_it: '', body_en: '', category: 'diritti', reliability: 'verified_fact', author: 'Redazione Tikkun', sources: [], tags: [], read_time_min: 5 };
const emptyProject = { title_it: '', title_en: '', summary_it: '', summary_en: '', status: 'planning', category: 'giustizia', budget_eur: 0, volunteers_needed: 0, roadmap: [] };
const emptyFinance = { type: 'donation', amount: 0, label: '', date: new Date().toISOString().slice(0, 10) };
const emptyChangelog = { date: new Date().toISOString().slice(0, 10), title: '', body: '' };

export default function Admin() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [tests, setTests] = useState([]);
  const [audit, setAudit] = useState([]);
  const [articles, setArticles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [finance, setFinance] = useState([]);
  const [changelog, setChangelog] = useState([]);

  const [editingArticle, setEditingArticle] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [newFinance, setNewFinance] = useState(null);
  const [newChangelog, setNewChangelog] = useState(null);

  const load = async () => {
    try {
      const [s, u, r, tt, a, ar, pr, tr] = await Promise.all([
        api.get('/admin/stats'), api.get('/admin/users'),
        api.get('/admin/help-requests'), api.get('/admin/testimonials'),
        api.get('/admin/audit'), api.get('/library/articles'),
        api.get('/projects'), api.get('/transparency'),
      ]);
      setStats(s.data); setUsers(u.data); setReqs(r.data); setTests(tt.data); setAudit(a.data);
      setArticles(ar.data); setProjects(pr.data);
      setFinance(tr.data.finance); setChangelog(tr.data.changelog);
    } catch (err) { toast.error(t.common.error); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/dashboard'); return; }
    load();
    // eslint-disable-next-line
  }, [user, loading, navigate]);

  const updateReqStatus = async (id, status) => {
    try {
      await api.patch(`/admin/help-requests/${id}`, { status });
      setReqs((rs) => rs.map((r) => r.id === id ? { ...r, status } : r));
      toast.success('Aggiornato');
    } catch { toast.error(t.common.error); }
  };
  const approveTest = async (id) => {
    try { await api.patch(`/admin/testimonials/${id}/approve`); setTests((ts) => ts.map(x => x.id === id ? { ...x, approved: true } : x)); toast.success('Approvata'); }
    catch { toast.error(t.common.error); }
  };
  const setRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}/role`, { role }); setUsers((us) => us.map(x => x.user_id === uid ? { ...x, role } : x)); toast.success('Ruolo aggiornato'); }
    catch (err) { toast.error(err?.response?.data?.detail || t.common.error); }
  };

  const saveArticle = async () => {
    try {
      const payload = { ...editingArticle };
      delete payload.id; delete payload.updated_at;
      payload.read_time_min = parseInt(payload.read_time_min) || 5;
      if (editingArticle.id) {
        await api.patch(`/admin/articles/${editingArticle.id}`, payload);
      } else {
        await api.post('/admin/articles', payload);
      }
      toast.success('Salvato');
      setEditingArticle(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t.common.error); }
  };
  const deleteArticle = async (id) => {
    if (!window.confirm('Eliminare articolo?')) return;
    try { await api.delete(`/admin/articles/${id}`); setArticles(a => a.filter(x => x.id !== id)); toast.success('Eliminato'); }
    catch { toast.error(t.common.error); }
  };

  const saveProject = async () => {
    try {
      const payload = { ...editingProject };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      payload.budget_eur = parseFloat(payload.budget_eur) || 0;
      payload.volunteers_needed = parseInt(payload.volunteers_needed) || 0;
      if (editingProject.id) await api.patch(`/admin/projects/${editingProject.id}`, payload);
      else await api.post('/projects', payload);
      toast.success('Salvato');
      setEditingProject(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t.common.error); }
  };
  const deleteProject = async (id) => {
    if (!window.confirm('Eliminare progetto?')) return;
    try { await api.delete(`/admin/projects/${id}`); setProjects(p => p.filter(x => x.id !== id)); toast.success('Eliminato'); }
    catch { toast.error(t.common.error); }
  };

  const saveFinance = async () => {
    try {
      const payload = { ...newFinance };
      payload.amount = parseFloat(payload.amount);
      await api.post('/admin/finance', payload);
      toast.success('Salvato');
      setNewFinance(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || t.common.error); }
  };
  const deleteFinance = async (id) => {
    if (!window.confirm('Eliminare voce?')) return;
    try { await api.delete(`/admin/finance/${id}`); setFinance(f => f.filter(x => x.id !== id)); toast.success('Eliminato'); }
    catch { toast.error(t.common.error); }
  };

  const saveChangelog = async () => {
    try {
      await api.post('/admin/changelog', newChangelog);
      toast.success('Salvato'); setNewChangelog(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || t.common.error); }
  };
  const deleteChangelog = async (id) => {
    if (!window.confirm('Eliminare voce?')) return;
    try { await api.delete(`/admin/changelog/${id}`); setChangelog(c => c.filter(x => x.id !== id)); toast.success('Eliminato'); }
    catch { toast.error(t.common.error); }
  };

  if (loading || !user || user.role !== 'admin' || !stats) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">{t.common.loading}</div>;
  }

  const tabs = [
    { key: 'overview', label: 'Panoramica', icon: ShieldCheck },
    { key: 'articles', label: 'Biblioteca', icon: FileText },
    { key: 'projects', label: 'Progetti', icon: Rocket },
    { key: 'finance', label: 'Bilanci', icon: TrendingUp },
    { key: 'changelog', label: 'Changelog', icon: ScrollText },
    { key: 'requests', label: 'Richieste', icon: Inbox },
    { key: 'testimonials', label: 'Testimonianze', icon: MessageSquareQuote },
    { key: 'users', label: 'Utenti', icon: Users },
    { key: 'audit', label: 'Audit', icon: ShieldCheck },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck size={22} className="text-brand-gold-dark" />
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Tikkun</p>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-tight">Amministrazione</h1>

      <div className="mt-10 flex flex-wrap gap-1 border-b border-border overflow-x-auto">
        {tabs.map(x => (
          <button key={x.key} onClick={() => setTab(x.key)} data-testid={`admin-tab-${x.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 whitespace-nowrap ${tab === x.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <x.icon size={14} /> {x.label}
          </button>
        ))}
      </div>

      <div className="mt-10" data-testid={`admin-panel-${tab}`}>
        {tab === 'overview' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Utenti', value: stats.users, icon: Users },
              { label: 'Richieste', value: stats.help_requests.total, icon: Inbox },
              { label: 'Testimonianze', value: `${stats.testimonials.total} · ${stats.testimonials.pending} in attesa`, icon: MessageSquareQuote },
              { label: 'Articoli', value: stats.articles, icon: FileText },
              { label: 'Progetti', value: stats.projects, icon: Rocket },
              { label: 'Contatti ricevuti', value: stats.contacts, icon: Inbox },
              { label: 'Collaboratori', value: stats.collaborators, icon: Users },
            ].map(c => (
              <div key={c.label} className="p-6 rounded-3xl border border-border bg-card">
                <c.icon size={18} className="text-muted-foreground mb-3" strokeWidth={1.75} />
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{c.label}</div>
                <div className="font-display text-2xl font-medium mt-1">{c.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* ARTICLES */}
        {tab === 'articles' && (
          <div>
            <div className="mb-4 flex justify-end">
              <button onClick={() => setEditingArticle({ ...emptyArticle })} data-testid="admin-new-article"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
                <Plus size={14} /> Nuovo articolo
              </button>
            </div>
            <div className="space-y-2">
              {articles.map(a => (
                <div key={a.id} data-testid={`admin-article-${a.id}`} className="p-4 rounded-2xl border border-border bg-card flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{a.title_it}</div>
                    <div className="text-xs text-muted-foreground">{a.category} · {a.reliability}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingArticle({ ...a })} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary"><Edit size={14} /></button>
                    <button onClick={() => deleteArticle(a.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-destructive/10 hover:text-destructive"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PROJECTS */}
        {tab === 'projects' && (
          <div>
            <div className="mb-4 flex justify-end">
              <button onClick={() => setEditingProject({ ...emptyProject })} data-testid="admin-new-project"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
                <Plus size={14} /> Nuovo progetto
              </button>
            </div>
            <div className="space-y-2">
              {projects.map(p => (
                <div key={p.id} data-testid={`admin-project-${p.id}`} className="p-4 rounded-2xl border border-border bg-card flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.title_it}</div>
                    <div className="text-xs text-muted-foreground">{p.status} · €{p.budget_eur} · {p.volunteers_needed} volontari</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingProject({ ...p })} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary"><Edit size={14} /></button>
                    <button onClick={() => deleteProject(p.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-destructive/10 hover:text-destructive"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FINANCE */}
        {tab === 'finance' && (
          <div>
            <div className="mb-4 flex justify-end">
              <button onClick={() => setNewFinance({ ...emptyFinance })} data-testid="admin-new-finance"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
                <Plus size={14} /> Nuova voce
              </button>
            </div>
            {newFinance && (
              <div className="mb-6 p-6 rounded-3xl border border-primary bg-card space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <select value={newFinance.type} onChange={(e) => setNewFinance({ ...newFinance, type: e.target.value })} className="px-3 py-2 rounded-xl bg-background border border-border">
                    <option value="donation">Donazione</option>
                    <option value="expense">Spesa</option>
                  </select>
                  <input type="number" step="0.01" placeholder="Importo" value={newFinance.amount} onChange={(e) => setNewFinance({ ...newFinance, amount: e.target.value })} className="px-3 py-2 rounded-xl bg-background border border-border" />
                  <input type="date" value={newFinance.date} onChange={(e) => setNewFinance({ ...newFinance, date: e.target.value })} className="px-3 py-2 rounded-xl bg-background border border-border" />
                  <input type="text" placeholder="Descrizione" value={newFinance.label} onChange={(e) => setNewFinance({ ...newFinance, label: e.target.value })} className="px-3 py-2 rounded-xl bg-background border border-border" />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveFinance} data-testid="admin-save-finance" className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium">Salva</button>
                  <button onClick={() => setNewFinance(null)} className="px-4 py-2 rounded-full text-sm">Annulla</button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {finance.map(f => (
                <div key={f.id} className="p-4 rounded-2xl border border-border bg-card flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {f.type === 'donation' ? <TrendingUp size={16} className="text-emerald-600" /> : <TrendingDown size={16} className="text-amber-600" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{f.label}</div>
                      <div className="text-xs text-muted-foreground">{f.date}</div>
                    </div>
                    <div className={`font-medium ${f.type === 'donation' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {f.type === 'donation' ? '+' : '-'}€{f.amount.toLocaleString()}
                    </div>
                  </div>
                  <button onClick={() => deleteFinance(f.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-destructive/10 hover:text-destructive"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CHANGELOG */}
        {tab === 'changelog' && (
          <div>
            <div className="mb-4 flex justify-end">
              <button onClick={() => setNewChangelog({ ...emptyChangelog })} data-testid="admin-new-changelog"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
                <Plus size={14} /> Nuova voce
              </button>
            </div>
            {newChangelog && (
              <div className="mb-6 p-6 rounded-3xl border border-primary bg-card space-y-3">
                <input type="date" value={newChangelog.date} onChange={(e) => setNewChangelog({ ...newChangelog, date: e.target.value })} className="w-full px-3 py-2 rounded-xl bg-background border border-border" />
                <input type="text" placeholder="Titolo" value={newChangelog.title} onChange={(e) => setNewChangelog({ ...newChangelog, title: e.target.value })} className="w-full px-3 py-2 rounded-xl bg-background border border-border" />
                <textarea rows={3} placeholder="Descrizione" value={newChangelog.body} onChange={(e) => setNewChangelog({ ...newChangelog, body: e.target.value })} className="w-full px-3 py-2 rounded-xl bg-background border border-border resize-none" />
                <div className="flex gap-2">
                  <button onClick={saveChangelog} className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium">Salva</button>
                  <button onClick={() => setNewChangelog(null)} className="px-4 py-2 rounded-full text-sm">Annulla</button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {changelog.map(c => (
                <div key={c.id} className="p-4 rounded-2xl border border-border bg-card flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">{c.date}</div>
                    <div className="font-medium">{c.title}</div>
                    <div className="text-sm text-muted-foreground mt-1">{c.body}</div>
                  </div>
                  <button onClick={() => deleteChangelog(c.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-destructive/10 hover:text-destructive"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* REQUESTS */}
        {tab === 'requests' && (
          <div className="rounded-3xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left py-3 px-4 font-medium">Titolo</th>
                  <th className="text-left py-3 px-4 font-medium hidden md:table-cell">Tipo</th>
                  <th className="text-left py-3 px-4 font-medium">Priorità</th>
                  <th className="text-left py-3 px-4 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {reqs.map(r => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="py-3 px-4 max-w-[300px] truncate">{r.title}</td>
                    <td className="py-3 px-4 text-muted-foreground hidden md:table-cell">{r.problem_type}</td>
                    <td className="py-3 px-4">{r.priority}</td>
                    <td className="py-3 px-4">
                      <select value={r.status} onChange={(e) => updateReqStatus(r.id, e.target.value)} className="px-3 py-1.5 rounded-full bg-secondary text-xs">
                        <option value="received">received</option>
                        <option value="in_progress">in_progress</option>
                        <option value="resolved">resolved</option>
                        <option value="closed">closed</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* TESTIMONIALS */}
        {tab === 'testimonials' && (
          <div className="space-y-3">
            {tests.map(x => (
              <div key={x.id} className="p-5 rounded-2xl border border-border bg-card flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground mb-1">{x.author_name} · {x.approved ? 'approved' : 'pending'}</div>
                  <p className="text-sm leading-relaxed">{x.body}</p>
                </div>
                {!x.approved && (
                  <button onClick={() => approveTest(x.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 text-xs font-medium hover:bg-emerald-500/20 transition-colors">
                    <Check size={12} /> Approva
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* USERS */}
        {tab === 'users' && (
          <div className="rounded-3xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left py-3 px-4 font-medium">Nome</th>
                  <th className="text-left py-3 px-4 font-medium">Email</th>
                  <th className="text-left py-3 px-4 font-medium hidden md:table-cell">Provider</th>
                  <th className="text-left py-3 px-4 font-medium">Ruolo</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-t border-border">
                    <td className="py-3 px-4">{u.name}</td>
                    <td className="py-3 px-4 text-muted-foreground">{u.email}</td>
                    <td className="py-3 px-4 text-muted-foreground hidden md:table-cell">{u.auth_provider}</td>
                    <td className="py-3 px-4">
                      <select value={u.role} onChange={(e) => setRole(u.user_id, e.target.value)} disabled={u.user_id === user.user_id}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${u.role === 'admin' ? 'text-brand-gold-dark bg-brand-gold/10' : 'bg-secondary'}`}>
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AUDIT */}
        {tab === 'audit' && (
          <div className="space-y-2">
            {audit.map(a => (
              <div key={a.id} className="p-4 rounded-xl border border-border bg-card text-sm font-mono">
                <span className="text-muted-foreground">{new Date(a.at).toLocaleString()}</span> · <span className="font-medium">{a.actor}</span> · <span className="text-brand-gold-dark">{a.action}</span>
                {a.meta && Object.keys(a.meta).length > 0 && <span className="text-muted-foreground"> · {JSON.stringify(a.meta)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Article editor modal */}
      {editingArticle && (
        <ArticleEditor
          value={editingArticle}
          onChange={setEditingArticle}
          onSave={saveArticle}
          onClose={() => setEditingArticle(null)}
        />
      )}
      {/* Project editor modal */}
      {editingProject && (
        <ProjectEditor
          value={editingProject}
          onChange={setEditingProject}
          onSave={saveProject}
          onClose={() => setEditingProject(null)}
        />
      )}
    </div>
  );
}

function ArticleEditor({ value, onChange, onSave, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-3xl border border-border max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-medium">{value.id ? 'Modifica articolo' : 'Nuovo articolo'}</h2>
          <button onClick={onClose} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary"><X size={16} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Titolo IT"><input value={value.title_it} onChange={(e) => onChange({ ...value, title_it: e.target.value })} /></Field>
            <Field label="Titolo EN"><input value={value.title_en} onChange={(e) => onChange({ ...value, title_en: e.target.value })} /></Field>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Estratto IT"><textarea rows={2} value={value.excerpt_it} onChange={(e) => onChange({ ...value, excerpt_it: e.target.value })} /></Field>
            <Field label="Estratto EN"><textarea rows={2} value={value.excerpt_en} onChange={(e) => onChange({ ...value, excerpt_en: e.target.value })} /></Field>
          </div>
          <Field label="Corpo IT"><textarea rows={6} value={value.body_it} onChange={(e) => onChange({ ...value, body_it: e.target.value })} /></Field>
          <Field label="Corpo EN"><textarea rows={6} value={value.body_en} onChange={(e) => onChange({ ...value, body_en: e.target.value })} /></Field>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Categoria">
              <select value={value.category} onChange={(e) => onChange({ ...value, category: e.target.value })}>
                {CATEGORY_KEYS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Attendibilità">
              <select value={value.reliability} onChange={(e) => onChange({ ...value, reliability: e.target.value })}>
                {RELIABILITY_KEYS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Autore"><input value={value.author} onChange={(e) => onChange({ ...value, author: e.target.value })} /></Field>
            <Field label="Tempo lettura (min)"><input type="number" value={value.read_time_min} onChange={(e) => onChange({ ...value, read_time_min: e.target.value })} /></Field>
          </div>
          <Field label="Fonti (una per riga)">
            <textarea rows={2} value={(value.sources || []).join('\n')} onChange={(e) => onChange({ ...value, sources: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })} />
          </Field>
          <Field label="Tag (virgola)">
            <input value={(value.tags || []).join(', ')} onChange={(e) => onChange({ ...value, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
          </Field>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2 rounded-full text-muted-foreground hover:text-foreground">Annulla</button>
          <button onClick={onSave} data-testid="admin-save-article" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground font-medium">Salva</button>
        </div>
      </div>
    </div>
  );
}

function ProjectEditor({ value, onChange, onSave, onClose }) {
  const addRoadmap = () => onChange({ ...value, roadmap: [...(value.roadmap || []), { title: '', done: false, date: '' }] });
  const updRoadmap = (i, k, v) => {
    const rm = [...(value.roadmap || [])]; rm[i] = { ...rm[i], [k]: v }; onChange({ ...value, roadmap: rm });
  };
  const delRoadmap = (i) => {
    const rm = [...(value.roadmap || [])]; rm.splice(i, 1); onChange({ ...value, roadmap: rm });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-3xl border border-border max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-medium">{value.id ? 'Modifica progetto' : 'Nuovo progetto'}</h2>
          <button onClick={onClose} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary"><X size={16} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Titolo IT"><input value={value.title_it} onChange={(e) => onChange({ ...value, title_it: e.target.value })} /></Field>
            <Field label="Titolo EN"><input value={value.title_en} onChange={(e) => onChange({ ...value, title_en: e.target.value })} /></Field>
          </div>
          <Field label="Riassunto IT"><textarea rows={3} value={value.summary_it} onChange={(e) => onChange({ ...value, summary_it: e.target.value })} /></Field>
          <Field label="Riassunto EN"><textarea rows={3} value={value.summary_en} onChange={(e) => onChange({ ...value, summary_en: e.target.value })} /></Field>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Stato">
              <select value={value.status} onChange={(e) => onChange({ ...value, status: e.target.value })}>
                {PROJECT_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Categoria"><input value={value.category} onChange={(e) => onChange({ ...value, category: e.target.value })} /></Field>
            <Field label="Budget (€)"><input type="number" value={value.budget_eur} onChange={(e) => onChange({ ...value, budget_eur: e.target.value })} /></Field>
            <Field label="Volontari cercati"><input type="number" value={value.volunteers_needed} onChange={(e) => onChange({ ...value, volunteers_needed: e.target.value })} /></Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Roadmap</label>
              <button onClick={addRoadmap} className="text-xs text-brand-gold-dark inline-flex items-center gap-1"><Plus size={12} /> Aggiungi tappa</button>
            </div>
            <div className="space-y-2">
              {(value.roadmap || []).map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="checkbox" checked={!!r.done} onChange={(e) => updRoadmap(i, 'done', e.target.checked)} />
                  <input placeholder="Titolo tappa" value={r.title} onChange={(e) => updRoadmap(i, 'title', e.target.value)} className="flex-1 px-3 py-2 rounded-xl bg-background border border-border" />
                  <input type="date" value={r.date || ''} onChange={(e) => updRoadmap(i, 'date', e.target.value)} className="px-3 py-2 rounded-xl bg-background border border-border" />
                  <button onClick={() => delRoadmap(i)} className="inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-destructive/10 hover:text-destructive"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2 rounded-full text-muted-foreground hover:text-foreground">Annulla</button>
          <button onClick={onSave} data-testid="admin-save-project" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-primary-foreground font-medium">Salva</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  const child = children;
  const cls = "w-full px-3 py-2 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none";
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground font-medium block mb-1.5">{label}</label>
      {typeof child === 'object' && child.type ? (
        <child.type {...child.props} className={cls} />
      ) : child}
    </div>
  );
}
